import type { ApiResponse } from "@vpm/shared-types";

const API_BASE = import.meta.env.VITE_API_URL ?? "";

const AUTH_LOGIN_PATHS = ["/admin/auth/login", "/staff/auth/login"];

/** Send unauthenticated users back to the unified login screen. */
function redirectToLoginIfSessionExpired(path: string, status: number): void {
  if (status !== 401) return;
  if (AUTH_LOGIN_PATHS.some((loginPath) => path.startsWith(loginPath))) return;

  const onLoginScreen =
    window.location.pathname === "/" ||
    window.location.pathname === "/login" ||
    window.location.pathname.endsWith("/login");

  if (onLoginScreen) return;

  window.location.replace("/");
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    cache: "no-store",
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    return {
      success: false,
      error: `Invalid response from server (${res.status})`,
    };
  }

  if (res.ok) {
    if (body.success !== false) body.success = true;
  } else {
    if (res.status === 401) {
      redirectToLoginIfSessionExpired(path, res.status);
    }
    if (!body.error) {
      body.error = `Request failed (${res.status})`;
      body.success = false;
    }
  }
  return body;
}

export const api = {
  admin: {
    login: (email: string, password: string) =>
      request<{ id: string; email: string; name: string; role: "admin" }>("/admin/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),
    logout: () => request("/admin/auth/logout", { method: "POST" }),
    me: () => request("/admin/auth/me"),
    dashboard: () => request("/admin/api/dashboard"),
    analytics: (query = "") => request(`/admin/api/dashboard/analytics${query}`),
    staff: (includeInactive = false, operationalOnly = false) => {
      const params = new URLSearchParams();
      if (includeInactive) params.set("includeInactive", "true");
      if (operationalOnly) params.set("operationalOnly", "true");
      // Avoid stale directory lists after create/update/delete in the same session.
      params.set("_", String(Date.now()));
      const q = params.toString();
      return request(`/admin/api/staff?${q}`);
    },
    createStaff: (data: unknown) =>
      request("/admin/api/staff", { method: "POST", body: JSON.stringify(data) }),
    updateStaff: (id: string, data: unknown) =>
      request(`/admin/api/staff/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    activateStaff: (id: string) => request(`/admin/api/staff/${id}/activate`, { method: "PATCH" }),
    deactivateStaff: (id: string) => request(`/admin/api/staff/${id}/deactivate`, { method: "PATCH" }),
    enableTracking: (id: string) => request(`/admin/api/staff/${id}/enable-tracking`, { method: "PATCH" }),
    disableTracking: (id: string) => request(`/admin/api/staff/${id}/disable-tracking`, { method: "PATCH" }),
    deleteStaff: (id: string) => request(`/admin/api/staff/${id}`, { method: "DELETE" }),
    staffOverview: (id: string) => request(`/admin/api/staff/${id}/overview`),
    departments: () => request("/admin/api/departments"),
    createDepartment: (data: unknown) =>
      request("/admin/api/departments", { method: "POST", body: JSON.stringify(data) }),
    updateDepartment: (id: string, data: unknown) =>
      request(`/admin/api/departments/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    deleteDepartment: (id: string) => request(`/admin/api/departments/${id}`, { method: "DELETE" }),
    jobTitles: () => request("/admin/api/job-titles"),
    createJobTitle: (data: unknown) =>
      request("/admin/api/job-titles", { method: "POST", body: JSON.stringify(data) }),
    updateJobTitle: (id: string, data: unknown) =>
      request(`/admin/api/job-titles/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    deleteJobTitle: (id: string) => request(`/admin/api/job-titles/${id}`, { method: "DELETE" }),
    roleKpiTemplates: (jobTitleId: string) => request(`/admin/api/job-titles/${jobTitleId}/kpi-templates`),
    listRoleKpiTemplates: (query = "") => request(`/admin/api/role-kpi-templates${query ? `?${query}` : ""}`),
    createRoleKpiTemplate: (data: unknown) =>
      request("/admin/api/role-kpi-templates", { method: "POST", body: JSON.stringify(data) }),
    updateRoleKpiTemplate: (id: string, data: unknown) =>
      request(`/admin/api/role-kpi-templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    deactivateRoleKpiTemplate: (id: string) =>
      request(`/admin/api/role-kpi-templates/${id}`, { method: "DELETE" }),
    bulkDeactivateKpiTemplates: (items: Array<{ id: string; source: "role" | "staff" }>) =>
      request<import("@vpm/shared-types").BulkActionResult>(`/admin/api/role-kpi-templates/bulk-deactivate`, {
        method: "POST",
        body: JSON.stringify({ items }),
      }),
    activateStaffKpiTemplate: (id: string) =>
      request(`/admin/api/role-kpi-templates/${id}/activate-staff-template`, { method: "PATCH" }),
    deactivateStaffKpiTemplate: (id: string) =>
      request(`/admin/api/role-kpi-templates/${id}/deactivate-staff-template`, { method: "PATCH" }),
    staffKpiTemplateAudit: (id: string) =>
      request(`/admin/api/role-kpi-templates/${id}/staff-template-audit`),
    tasks: (query = "") => request(`/admin/api/tasks${query}`),
    getTask: (id: string) => request(`/admin/api/tasks/${id}`),
    resolveTaskBlocker: (taskId: string, blockerId: string, data: { resolutionMessage: string }) =>
      request(`/admin/api/tasks/${taskId}/blockers/${blockerId}/resolve`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    addTaskComment: (id: string, data: { body: string }) =>
      request(`/admin/api/tasks/${id}/comments`, { method: "POST", body: JSON.stringify(data) }),
    handoffTask: (id: string, data: { toStaffUserId: string; handoffNote: string }) =>
      request(`/admin/api/tasks/${id}/handoff`, { method: "POST", body: JSON.stringify(data) }),
    assignTask: (data: unknown) =>
      request("/admin/api/tasks/assign", { method: "POST", body: JSON.stringify(data) }),
    cancelTask: (id: string) => request(`/admin/api/tasks/${id}/cancel`, { method: "POST" }),
    bulkCancelTasks: (ids: string[]) =>
      request<import("@vpm/shared-types").BulkActionResult>(`/admin/api/tasks/bulk-cancel`, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    taskStats: () => request("/admin/api/tasks/stats"),
    workLogsMissed: () => request("/admin/api/work-logs/missed-today"),
    workLogs: (query = "") => request(`/admin/api/work-logs${query}`),
    alerts: (unreadOnly = false, offset = 0, limit = 25) => {
      const params = new URLSearchParams();
      if (unreadOnly) params.set("unreadOnly", "true");
      if (offset > 0) params.set("offset", String(offset));
      if (limit !== 25) params.set("limit", String(limit));
      const qs = params.toString();
      return request<import("./alerts-types").AlertListPage>(`/admin/api/alerts${qs ? `?${qs}` : ""}`);
    },
    alertUnreadCount: () => request<{ count: number }>("/admin/api/alerts/unread-count"),
    markAlertRead: (id: string) => request(`/admin/api/alerts/${id}/read`, { method: "PATCH" }),
    resolveBlockerAlert: (id: string, data: { resolutionMessage: string }) =>
      request(`/admin/api/alerts/${id}/resolve-blocker`, { method: "POST", body: JSON.stringify(data) }),
    markAllAlertsRead: () => request("/admin/api/alerts/read-all", { method: "POST" }),
    evaluateAlerts: () => request("/admin/api/alerts/evaluate-missed-logs", { method: "POST" }),
    evaluateTaskAlerts: () =>
      request("/admin/api/tasks/evaluate-overdue-alerts", { method: "POST" }),
    kpis: () => request("/admin/api/kpis"),
    kpiAccountability: (query = "") => request(`/admin/api/kpis/accountability${query}`),
    evaluateKpiAlerts: () =>
      request("/admin/api/kpis/evaluate-alerts", { method: "POST" }),
    status: () => request("/admin/api/status"),
    recomputeStatus: () => request("/admin/api/status/recompute", { method: "POST" }),
    alertSettings: () =>
      request<{
        emailNotificationsEnabled: boolean;
        personalEmailNotificationsEnabled?: boolean;
        smtpConfigured: boolean;
        emailSendingEnabled: boolean;
      }>("/admin/api/alerts/settings"),
    updateAlertSettings: (data: {
      emailNotificationsEnabled?: boolean;
      personalEmailNotificationsEnabled?: boolean;
    }) => request("/admin/api/alerts/settings", { method: "PATCH", body: JSON.stringify(data) }),
    handoffPolicies: () => request<import("./alerts-types").HandoffPolicies>("/admin/api/tasks/handoff-policies"),
    updateHandoffPolicies: (data: import("./alerts-types").HandoffPolicies) =>
      request("/admin/api/tasks/handoff-policies", { method: "PATCH", body: JSON.stringify(data) }),
    jobs: () => request("/admin/api/jobs"),
    runJob: (name: string) => request(`/admin/api/jobs/run/${name}`, { method: "POST" }),
  },
  staff: {
    login: (email: string, password: string) =>
      request<{ id: string; email: string; name: string; role: "staff_user" }>("/staff/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      }),
    logout: () => request("/staff/auth/logout", { method: "POST" }),
    me: () => request("/staff/auth/me"),
    updateTimezone: (timezone: string) =>
      request("/staff/auth/timezone", { method: "PATCH", body: JSON.stringify({ timezone }) }),
    tasks: () => request("/staff/api/tasks"),
    getTask: (id: string) => request(`/staff/api/tasks/${id}`),
    taskStats: () => request("/staff/api/tasks/stats"),
    createTask: (data: unknown) =>
      request("/staff/api/tasks", { method: "POST", body: JSON.stringify(data) }),
    updateTask: (id: string, data: unknown) =>
      request(`/staff/api/tasks/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    addTaskBlocker: (id: string, data: { message: string }) =>
      request(`/staff/api/tasks/${id}/blockers`, { method: "POST", body: JSON.stringify(data) }),
    addTaskComment: (id: string, data: { body: string }) =>
      request(`/staff/api/tasks/${id}/comments`, { method: "POST", body: JSON.stringify(data) }),
    handoffTargets: () =>
      request<Array<{ id: string; name: string; email: string; department: { name: string } }>>(
        "/staff/api/tasks/handoff-targets",
      ),
    handoffTask: (id: string, data: { toStaffUserId: string; handoffNote: string }) =>
      request<{ leftAssignee?: boolean; task?: unknown }>(`/staff/api/tasks/${id}/handoff`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    cancelTask: (id: string) => request(`/staff/api/tasks/${id}/cancel`, { method: "POST" }),
    bulkCancelTasks: (ids: string[]) =>
      request<import("@vpm/shared-types").BulkActionResult>(`/staff/api/tasks/bulk-cancel`, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    kpiTemplateAudit: (id: string) => request(`/staff/api/my-kpi-templates/${id}/audit`),
    listMyKpiTemplates: (query = "") => request(`/staff/api/my-kpi-templates${query ? `?${query}` : ""}`),
    createMyKpiTemplate: (data: unknown) =>
      request("/staff/api/my-kpi-templates", { method: "POST", body: JSON.stringify(data) }),
    updateMyKpiTemplate: (id: string, data: unknown) =>
      request(`/staff/api/my-kpi-templates/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    deactivateMyKpiTemplate: (id: string) =>
      request(`/staff/api/my-kpi-templates/${id}`, { method: "DELETE" }),
    bulkDeactivateMyKpiTemplates: (ids: string[]) =>
      request<import("@vpm/shared-types").BulkActionResult>(`/staff/api/my-kpi-templates/bulk-deactivate`, {
        method: "POST",
        body: JSON.stringify({ ids }),
      }),
    myKpiTemplates: () => request("/staff/api/my-kpi-templates"),
    jobTitles: () => request("/staff/api/job-titles"),
    workLogToday: () =>
      request<import("@vpm/shared-types").WorkLogTodayResponse>("/staff/api/work-logs/today"),
    workLogSuggestions: () => request<import("@vpm/shared-types").WorkLogSuggestions>("/staff/api/work-logs/suggestions"),
    createWorkLog: (data: unknown) =>
      request("/staff/api/work-logs", { method: "POST", body: JSON.stringify(data) }),
    updateWorkLog: (id: string, data: unknown) =>
      request(`/staff/api/work-logs/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    kpis: () => request("/staff/api/kpis"),
    kpiAccountability: (query = "") => request(`/staff/api/kpis/accountability${query}`),
    kpiStats: () => request("/staff/api/kpis/stats"),
    alerts: (unreadOnly = false, offset = 0, limit = 25) => {
      const params = new URLSearchParams();
      if (unreadOnly) params.set("unreadOnly", "true");
      if (offset > 0) params.set("offset", String(offset));
      if (limit !== 25) params.set("limit", String(limit));
      const qs = params.toString();
      return request<import("./alerts-types").AlertListPage>(`/staff/api/alerts${qs ? `?${qs}` : ""}`);
    },
    alertUnreadCount: () => request<{ count: number }>("/staff/api/alerts/unread-count"),
    markAlertRead: (id: string) => request(`/staff/api/alerts/${id}/read`, { method: "PATCH" }),
    markAllAlertsRead: () => request("/staff/api/alerts/read-all", { method: "POST" }),
    alertSettings: () =>
      request<{ emailNotificationsEnabled: boolean; smtpConfigured: boolean; emailSendingEnabled: boolean }>(
        "/staff/api/alerts/settings",
      ),
    updateAlertSettings: (data: { emailNotificationsEnabled: boolean }) =>
      request("/staff/api/alerts/settings", { method: "PATCH", body: JSON.stringify(data) }),
    status: () => request("/staff/api/status"),
    accountabilityOverview: () =>
      request<import("@vpm/shared-types").StaffOverview>("/staff/api/accountability"),
  },
};
